import { ReactElement, ReactNode } from "react";
import { MAP_ELEMENT } from "../../contexts/WorldMapContext/constants";
import { DELETE_TYPE } from "../../pages/PlanOperationPage/Popup/Clear";
import {
    ACTION_TYPE,
    AVAILABLE_TIME_TYPE,
    CHAT_TYPE,
    COMBINE_TREATMENT_LEVEL,
    CONTRACT_PERMISSION_TYPE,
    CONTRACT_TYPE,
    CUSTOMER_TYPE,
    DELIVERY_TYPE,
    DISEASE_TYPE,
    DISEASED_ZONE,
    EQUIPMENT_TYPE,
    EVENT_TYPE,
    EXECUTION_TYPE,
    FISH_HEALTH,
    KeyChangeLog,
    KeyOfStep,
    LineStatus,
    LOG_STATUS,
    MESSAGE_STATUS,
    NOTE_TYPE,
    NotificationType,
    OP_RUNNING_MODE,
    OP_STATUS,
    OPERATION_CONFIRM_STATUS,
    OPERATION_TYPE,
    PLAN_MODE,
    PriorityTime,
    PROCESS_TYPE,
    ROUND_JOB, SensorGroupType, SERVICE_TASK,
    SITE_DISEASE_STATUS,
    SITE_TYPE,
    SSO_MODE,
    STEP_STATUS,
    TASK_TYPE,
    TREATMENT_TYPE,
    TYPE_MATERIAL_TRANSPORT,
    USER_ROLE,
    VALIDATION_STATUS,
    VESSEL_TYPE,
    WEATHER_STATUS
} from "./constants";
import { NotifyCode } from "./message";

export interface IPosition {
    longitude: number
    latitude: number
    depth?: number
}

export interface IProductionArea {
    id: string
    name: string
    coordinates: [][]
    color: string
}

export interface IServiceTask {
    id: string
    name: string
    duration?: number
    capabilities: ICapability[]
}

export interface ICapability {
    id: string
    name: string
    type: EQUIPMENT_TYPE
    group_type?: string
}

export interface IPois {
    id: string
    name: string
    field?: MAP_ELEMENT
    type?: MAP_ELEMENT
    position: IPosition
    region_id: string
    phone?: string
    work_time?: number | '-'
    capabilities: { id: string, name: string }[]
    diseases?: {
        diseases?: any[]
        production_area?: {
            id: number
            name: string
        }
    }
}

export interface IDisease {
    id: string
    name: string
    description: string
    is_active: boolean
}

export interface IDiseaseZone {
    diseasedType: DISEASED_ZONE
    forskNavn: string
    forskLink: string
    forskNr: string
    originalDate: string
    fromDate: string
}

export interface IFactory {
    id?: string
    name: string
    address: string
    phone: string
    work_time: number
    tag: string
    capacity_discharge: number
    plan: any[]
    position: IPosition
    deliver_type: {
        [id: string]: IDeliveryType
    }
    inventory?: IMaterial[]
    tenant_id?: string
}

export interface ISite {
    id: string
    name: string
    is_empty?: boolean
    code?: number
    type?: SITE_TYPE
    work_time?: number
    phone?: string
    position: IPosition
    address: string
    site_type: string
    region_id: string
    primary_factory?: string
    primary_factory_id?: string
    units: IUnit[]
    diseases?: {
        cases?: { disease: DISEASE_TYPE, status: SITE_DISEASE_STATUS }[]
        disease_zone?: string
        diseases: any[]
        lice: ILiceInfo
    }[] | any
    prod_area?: string
    inventory?: IMaterial[]
    unit_diseases?: {
        types: any[]
    }
    starvings?: IUnit['starvings']
    tenant_id?: string
}

export interface IUnit {
    id: string
    unit_id: string
    fish_amount: number
    avg_weight: number
    total_weight: number
    mortality_risk: boolean

    fish_amount_original?: number
    harvest_id?: string
    delivery_id?: string
    fish_health?: FISH_HEALTH | null
    type?: string
    process_type?: PROCESS_TYPE
    checked?: boolean
    delivery_types?: IDeliveryTypeUnit[]
    unit_diseases?: IUnitDisease
    note?: string
    site_id?: string
    site_name?: string
    factory_id?: string
    factory_name?: string
    harvest_date?: string
    packing_date?: string
    diseases?: any
    fish_group?: string
    primary_factory?: string
    site_type?: SITE_TYPE
    storages?: IUnitStorage[]
    starving_days?: number
    lice?: ILiceInfo
    lice_reduction?: any
    mortality_increase?: any
    density_percent?: number
    tank_number?: number
    round_number?: number
    starvings?: {
        timestamp: number
        starving_days: number
        unit_id?: string
    }[]
    selected?: boolean                          // Operations table - Site chart - Plan page
}

export interface IDeliveryTypeUnit extends IDeliveryType {
    unit?: string,
    fish_amount: number,
    total_weight: number,
    unit_id?: string
    tanks: {
        id: string
        name?: string
        fish_amount?: number
        total_weight?: number
    }[]
}

export interface IDeliveryType {
    id: string
    name: string
    type?: DELIVERY_TYPE
    default?: boolean
    speed?: number
}

export interface IUnitDisease {
    site_id?: string
    types: IDisease[]
}

export interface IUnitStorage extends IStorage {
    density: number
    total_weight: number
    fish_amount: number
}

export interface ISchedule {
    name: string
    localityNo?: any
    disease?: any
    diseases?: any
    time: number
    location_type: "disease" | "site" | "factory"
    status: SCHEDULE_STATUS
}

export enum SCHEDULE_STATUS {
    ENTER = 'enter',
    LEAVE = 'leave'
}

export interface IChecklist {
    id?: string
    name?: string
    content: string

    required_map?: {
        vessel_names: { id: string, name: string, type: VESSEL_TYPE }[]
        vessel_types: string[]
        sub_types: string[]
        steps: string[]
    }
    signer: { [id: string]: string }
    sign_roles?: string[]
    step_name?: string
    result?: string
    is_active?: boolean
    version?: string
    group_id?: string
}

export interface IEquipment {
    types: TREATMENT_TYPE[]

    [id: string]: any
}

export interface IVessel {
    id: string
    name: string
    contact?: string
    contracts: IContract[]
    capabilities: any[]
    capacity: number
    operation_type: OPERATION_TYPE[]
    primary_factory: string
    type: VESSEL_TYPE
    treatment_type: any[]
    operation_sub_types: TREATMENT_TYPE[]
    tenant_id: string
    index: number
    isShow?: boolean
    isOwn?: boolean
    enable_auto?: boolean
    enable_spot?: boolean
    image_url?: string
    permission: any
    available_times: IAvailableTime[]
    available_time_type: AVAILABLE_TIME_TYPE
    iot_name?: string
    gps?: {
        [id: string]: any
    }
    row?: number
    storages?: IStorage[]
    priority?: PriorityTime
    isPermission?: boolean
    harvest_capacity?: number
    factory_discharge?: number
    waiting_unit_capacity?: number
    cleaning_time?: number
    hourly_cost?: number | null
    cleaning_agent?: number | null
    start_point?: {
        id: string
        type: 'poi' | 'site' | 'factory'
        name: string
    } | Record<string, never>
    position?: {
        latitude: number
        longitude: number
    }
    tank_sub_types?: TREATMENT_TYPE[]
    density_map?: IDensityMap
    equipments?: IEquipment[]
    vessel_owner?: string
    width?: number
    length?: number
    speed?: number
    region_names?: string[]
    checklists?: IChecklist[]
    checklists_v2?: IChecklist[]
    vessel_owner_id?: string
}

export interface ILiceInfo {
    female_lice?: { value: number, state?: LineStatus },
    mobile_lice?: { value: number, state?: LineStatus },
    stationary_lice?: { value: number, state?: LineStatus },
    total_lice?: { value: number, state?: LineStatus },
    tenant_id?: string,
    week?: string
}

export interface IDiseaseOfSite {
    cases: any[]
    disease_zone: any //"surveillance"

    diseases: any
    lice?: ILiceInfo
    id: string
    name: string
    pk: string
    production_area: {
        name: string
        id: number
    }
    site_id: string
    site_number: string
}

export interface IPause {
    start_time: number
    finish_time?: number
    reason?: string
}

interface IDetailOperation {
    id: string
    operation_code: string
    operation_type: OPERATION_TYPE
    status: OP_STATUS
    tenant_id: string
    current_process: number

    approver?: string
    certain_mode?: any
    diseases?: IDiseaseOfSite[]
    is_active_vessel?: boolean
    checklists?: { [id: string]: IChecklist }
    checklists_v2?: IChecklist[]
    checklist_ids?: string[]
    tenant_name?: string
    typical_condition?: WEATHER_STATUS
    sorting?: boolean
    router_id?: string
    contract_id?: string
    available_time_id?: string
    avg_duration?: number
    mortality_increase?: number
    lice_reduction?: {
        stationary_lice?: number
        mobile_lice?: number
        female_lice?: number
    }
    route?: IRoute
    note?: string

    related_id?: string

    // Chart
    processes?: IActivityLog[]

    vessel_id?: string
    vessel_name?: string
    vessel_type?: VESSEL_TYPE

    eta?: {                                         // For dashboard
        operation_id: string
        time: number
        step: string
    }

    files?: {
        key: string
        name: string
    }[]

    execution_type?: EXECUTION_TYPE
    latest_time?: number
    pauses?: IPause[]
    dead_fish?: number
    grading_status?: string
    invoices?: {
        operation_id: string
        tenant_id: string
        operation_code: string
        operation_type: OPERATION_TYPE
        event_type?: EVENT_TYPE
    }[]
    billings?: {
        operation_id?: string
        name: string
        duration: number
        cost: number
        actual_duration?: number
    }[]
    plan_mode?: PLAN_MODE
    hourly_cost?: number | null
    cleaning_agent?: number | null
    start_point?: IVessel['start_point']
    start_point_ts?: number
    avg_speed?: number
}

export interface IUnitSummary {
    total_weight: number;
    fish_amount: number;
    tanks: IUnitSummaryTank[];
    dead_fish?: number
    mortality_increase?: number
    lice_reduction?: {
        stationary_lice?: number
        mobile_lice?: number
        female_lice?: number
    }
}

export interface IUnitSummaryTank {
    tank_id: string;
    name: string;
    load: { total_weight: number, fish_amount: number }[];
    discharge: IUnitSummaryDischarge[];
}

export interface IUnitSummaryDischarge {
    fish_amount: number;
    total_weight: number;
    delivery_type_id: string;
    name: string;
}

export interface IHarvest extends IDetailOperation {
    duration_est?: number
    site_id: string
    site_name: string
    sub_operations: IUnit[]
    factory_id: string
    factory_name: string
    router_id?: string
    is_waiting_unit?: boolean
    op_running_mode?: OP_RUNNING_MODE
    prev_op_router_id?: string
    start_place_id?: string
    start_place_field?: string
    tanks: {
        id: string
        name: string
        density?: number
        real_density?: number
        max_density?: number
        total_weight?: number
        fish_amount?: number
    }[]
    total_weight: number
    fish_amount: number
    isInternal?: boolean
    site?: any
    source?: any
    sites?: { id: string, name: string }[]
    factory?: any
    destination?: any
    source_site_type?: SITE_TYPE
    total_amount?: number
    delivery_types?: any
    is_journey_to_site?: boolean
    is_cleaning?: boolean
    error_detail?: any
    summary?: {
        unit_id: string
        plan: IUnitSummary
        actual: IUnitSummary
    }[]
}

export interface ITreatment extends IDetailOperation {
    duration_est?: number
    site: ISite
    site_name: string
    sub_operations: IUnit[]
    support_vessel: any
    is_cleaning: boolean
    tasks: TTaskTreatment[]
    unit_order?: {
        unit_id: string
        id: string
    }[]
    round_map?: Record<string, IRoundPlan>
    real_round_map?: Record<string, IRoundPlan>
    real_sub_operations?: {
        unit_id: string
        fish_amount?: number
        total_weight?: number
        avg_weight?: number
        tasks?: {
            treatment_type?: TREATMENT_TYPE
            type: TASK_TYPE
            destination_name?: string
        }[]
        round_number?: number
        dead_fish?: number
    }[]
    duration?: number
    dead_fish?: number
}

export interface ISubDestination extends IUnit {
    delivery_id?: string
    delivery_name?: string
}

export interface ITransport extends IDetailOperation {
    router_id?: string
    prev_op_router_id?: string
    start_place_id?: string
    start_place_field?: string
    source_id: string
    source_name: string
    destination_id?: string
    destination_name?: string
    sub_destinations?: ISubDestination[]
    source_site_type?: SITE_TYPE
    destination_site_type?: SITE_TYPE
}

export interface IEvent extends IDetailOperation {
    event_type: EVENT_TYPE
    note: string
    parallel?: boolean
    pois?: IPois[] | { id: string, name: string }[]
}

export interface IService extends IDetailOperation {
    duration_est?: number
    parallel?: boolean
    sub_operations: IUnit[]
    tasks?: ITaskService[]
    request_tab_id?: string
    crew_tasks?: {
        id: string
        description: string
        est_start_time?: number
        est_finish_time?: number
        real_start_time?: number
        real_finish_time?: number
        error?: Record<string, { message: string }>
    }[]
}

export interface ISupportOp extends IDetailOperation {
    related_id: string
    locations: {
        id: string
        name: string
        type: MAP_ELEMENT.SITE | MAP_ELEMENT.FACTORY | MAP_ELEMENT.POI
        sub_locations?: {
            id: string
            name: string
        }[]
    }[]
}

export type TOperation = IHarvest & ITreatment & ITransport & IEvent & IService & ISupportOp

export interface IOperation {
    activity_log: IActivityLog[]
    operation: TOperation
    vessel: IVessel

    status?: VALIDATION_STATUS                      // For plan page
    message?: string                                // For plan page
    isShow?: boolean                                // For plan page
    loading?: boolean                               // For edit op

    action_type?: OPERATION_CONFIRM_STATUS          // For plan page
    action?: DELETE_TYPE                            // For plan page
    est_start_time?: number                         // For tracking operation
    checklists_v2?: IChecklist[]                    // For tracking operation
    route?: IRoute                                  // For operation detail
}

export interface IWellboatVessel {
    next_operation: IOperation | null
    prev_operation: IOperation | null
    vessel: IVessel
}

export interface IWellboatsResponse {
    operations: IOperation[]
    vessels: IWellboatVessel[]
    before_count: boolean
    after_count: boolean
}

export interface IOperations {
    [index: string]: IOperation
}

export interface IWellboats extends Omit<IWellboatsResponse, 'operations' | 'vessels'> {
    operations: IOperations
    vessels: IVessel[]
}

export interface ILevelByVessel {
    [vesselId: string]: ILevelByOp
}

export interface ILevelByVessel2 {
    [vesselId: string]: ILevelByGroup
}

export interface ILevelByGroup {
    [groupId: string]: ILevelByOp2
}

export interface ILevelByOp2 {
    [opId: string]: ILevel2
}
export interface ILevel2 {
    operation_code: string
    index: number           // Column index of this operation within the overlap group
    count: number           // Total number of columns (max overlap count) in the overlap group
}

export interface ILevelByOp {
    [opId: string]: ILevel
}

export interface ILevel {
    main: boolean
    operation_code: string
    level: number
    sub: number
    ids: string[]
}

export interface ISubStepActivityLog {
    est_start_time: number
    real_start_time?: number
    est_finish_time: number
    real_finish_time?: number
    group_id: string
    status?: STEP_STATUS
    delivery_type_id?: string
    sub_id?: string
    tank_id: string
    discharge_type?: DELIVERY_TYPE
    total_weight?: number
    fish_amount?: number
}

export interface IActivityLog {
    key?: KeyOfStep | any
    index?: number
    place?: string
    arrive_time?: number                                // Time from gps of vessel   
    depature_time?: number                              // Time from gps of vessel   
    est_start_time: number
    real_start_time?: number
    est_finish_time?: number
    real_finish_time?: number
    sub_steps?: ISubStepActivityLog[]
    real_sub_steps?: {
        group_id?: string
        real_start_time?: number
        real_finish_time?: number
        tank_id: string
        tank_area_id?: string
        unit_id?: string
        delivery_type_id?: string
        delivery_type_name?: string
        fish_amount?: number
        total_weight?: number
        dead_fish?: number
    }[]
    eta?: number
    duration?: number
    resting_duration?: number
    group_id?: string
    unit_id?: string
    round?: string
    name?: string
    real_round_map?: Record<string, IRoundPlan>
}

export interface IGroupActivityLog {
    id: string
    name: string
    list: IActivityLog[]
}

export interface INoteCalender {
    id: string
    level: NOTE_TYPE
    note: string
    tenant_id: string
    timestamp: number
    user: {
        username: string
        roles: USER_ROLE[]
    }
}

export interface IStorage {
    id?: string
    name: string
    type: EQUIPMENT_TYPE
    value?: number
    speed?: number
    group_id?: string
    group_name?: string
    group_type?: any
    materials?: any[]
}

export interface IAvailableTime {
    id: string
    start_time: number
    finish_time: number
    vessel_id: string
}

export interface IContract {
    id: string
    type: CONTRACT_TYPE
    tenant_id: string
    start_time: number
    finish_time: number
    due_time: number
    contract_code: string
    permission: CONTRACT_PERMISSION_TYPE
}

export interface ITenant {
    id: string
    tenant_id: string
    name: string
    weather_license?: boolean
    customer_type: CUSTOMER_TYPE
}

export interface IPropsChildren {
    children?: ReactNode | ReactElement
}

export interface IPoint {
    longitude: number
    latitude: number
    depth?: number
    point_id?: number
    name?: string
}

export interface IRoute {
    id: string
    source: any
    source_id: any
    destination: any
    destination_id: any
    default: boolean
    name: string
    distance: number
    points: IPoint[]
    type?: any
    diseases?: DISEASED_ZONE[]
    route_mode?: 'manual' | 'auto'
}

export interface IRegion {
    id: string
    name: string
}

export interface IDomain {
    domain: string
    provider_name: string
    sso_mode: SSO_MODE
}

export interface IGroupChat {
    field: string
    host_id: string
    id: string
    members: {
        id: string
        username: string
    }[]
    name: string
}

export interface IMessage {
    id: string
    chat_id: string
    message: string
    roles: USER_ROLE[]
    timestamp: number
    user_tenant_name: string
    username: string
    seen?: {
        [id: string]: {
            timestamp: number
            username: string
        }
    }
    status?: MESSAGE_STATUS
    sending_status?: string
}

export interface IMessages {
    [groupId: string]: IChatBox
}

export interface IChatBox {
    id: string
    loading: boolean
    prev?: boolean
    next?: boolean
    messages: IMessage[]
}

export interface IConversation {
    id: string
    group_id: string
    group_chat: IGroupChat
    chat_id?: string
    message: IMessage
    message_id: string
    status: MESSAGE_STATUS
    timestamp: number
    type: CHAT_TYPE
}

export interface IIdName {
    id: any
    name: string
}

export interface IIdName2<S> {
    id: S
    name: string
}

export interface IIdNameObject<S> {
    [id: string]: {
        id: S
        name: string
    }
}

export interface IWeather {
    id: string
    name: string
    is_enable: boolean
    type: string
    group_id: string
    bad_condition: string
    moderate_condition: string
    good_condition: string
    actionType?: ACTION_TYPE
}

export interface ISTHP {
    harvest_id: string
    status: string
    unit_number: string
    factory_id: string
    factory_name: string
    fish_amount: number
    harvest_date: string
    original_fish_amount: number
    packing_date: string
    planned_amount: number
    region_id: string
    site_id: string
    site_name: string
    tenant_id: string
    total_weight: number
    unit_id: string
}

export interface ISubCombineTreatment {
    name: string
    type: TASK_TYPE
    treatment_type: TREATMENT_TYPE
    duration: number
    durationTxt?: string
}

export interface ICombineTreatment {
    id: string
    name: string
    sub_types: ISubCombineTreatment[]
    tenant_id: number
    level: COMBINE_TREATMENT_LEVEL
    type: TASK_TYPE.COMBINE
}

export interface IDiffItem {
    new: any
    old: any
    state: LOG_STATUS
}

export interface IDiffUnits {
    [unitId: string]: {
        unit_id: string             // name of unit
        state: LOG_STATUS
        [KeyChangeLog.TASKS]?: IDiffItem[],
        [KeyChangeLog.TOTAL_WEIGHT]?: {
            new: number
            old: number
        }
        [KeyChangeLog.FISH_AMOUNT]?: {
            new: number
            old: number
        }
        [KeyChangeLog.NOTE]?: {
            new: number
            old: number
            state: LOG_STATUS
        }
        [KeyChangeLog.DELIVERY_TYPES]?: {
            new: {
                id: string
                name: string
                [key: string]: any
            }[]
            old: {
                id: string
                name: string
                [key: string]: any
            }[]
        }
    }
}

export interface IDifferenceLogs {
    [KeyChangeLog.SUB_OPS]?: IDiffUnits

    [KeyChangeLog.DURATION]?: {
        new: number
        old: number
        state: LOG_STATUS
    }

    [KeyChangeLog.START_TIME]?: {
        new: number
        old: number
        state: LOG_STATUS
    }

    [KeyChangeLog.FINISH_TIME]?: {
        new: number
        old: number
        state: LOG_STATUS
    }

    [KeyChangeLog.TASKS]?: {
        new: number
        old: number
        state: LOG_STATUS
    }

    [KeyChangeLog.STATUS]?: {
        new: OP_STATUS
        old: OP_STATUS
        state: LOG_STATUS
    }
    [KeyChangeLog.DELAY_TIME]?: {
        new: {
            key: string
            time_late: number
        }
        state: LOG_STATUS
    }

    [key: string]: any
}

export interface IOperationLog {
    differences: IDifferenceLogs
    operation_type: OPERATION_TYPE
    operation_id: string
    operation_code: string
    site_name: string
    vessel_id: string
    state: LOG_STATUS
}

export interface IChangeLog {
    timestamp: number
    username: string
    operation: {
        [id: string]: IOperationLog
    }
}


export interface INotification {
    id: string
    type: string
    message: string
    notificationType?: NotificationType
    timestamp: number
    detail: any
    read: boolean
    date_map?: {
        key: string
        value: number
    }[]
}

export interface IVesselConfig {
    delivery_type: DELIVERY_TYPE,
    priority: PriorityTime,
    is_journey_to_site: boolean,
    cleaning_types: DELIVERY_TYPE[]
}

export interface IAfterProcesses {
    type: ROUND_JOB
    duration?: number
    destination_name?: string
    place?: string
    destination_id?: string
    start_time?: number
    finish_time?: number
    index?: number
}

export interface IRoundPlan {
    real_start_time?: number
    real_finish_time?: number
    tanks: {
        tank: string
        density?: number
        total_weight?: number
        start_places?: string[]
        finish_places?: string[]
        max_density?: number
        destination_name?: string
        round?: number
        type?: string
        id_unit?: string
        start_time?: number
        finish_time?: number
        processes?: {
            start_time: number
            finish_time: number
            place?: string
            unit_id: string
            round?: string
            index?: number
            duration?: number
            destination_name?: string
            keys: string[]
            real_start_time?: number
            type?: string
        }[]
    }[],
    after_processes: IAfterProcesses[]
    vessel_movements?: {
        start_time: number
        type: 'arrive' | 'departure'
    }[]
}

export type TProcessItem = NonNullable<IRoundPlan['tanks'][number]['processes']>[number];

export interface IConditionOfSorting {
    value: [[string, number] | null, [string, number] | null]
    middle_task?: null | string
    middle_tank_name?: string

    [id: string]: any
}

export interface ITaskTreatment {
    group_id: string
    type: TASK_TYPE | null
    type_id?: string
    treatment_type?: TREATMENT_TYPE
    type_name?: string
    destination_id?: string
    destination_name?: string
    duration?: number
    sorting?: boolean
    sub_operation: {
        unit_id: string
        id: string
        site_id: string
        site_name: string
    }
    sub_operations?: {
        unit_id: string
        id: string
        site_id: string
        site_name: string
        process_type?: PROCESS_TYPE
        density_percent?: number
    }[]
    error?: any
    index?: number
    elementId?: number
    support_vessels?: any[]
}

export interface ICombineTreatment extends ITaskTreatment {
    sub_tasks: ITaskTreatment[]
}

export interface ISorting extends ITaskTreatment {
    conditions: {
        [id: string]: IConditionOfSorting
    }
    sub_tasks: { [condition: string]: TTaskTreatment[] }
}

export type TTaskTreatment = ITaskTreatment | ICombineTreatment | ISorting

export interface ITaskService {
    location_id: string,
    location_name: string,
    location_type: MAP_ELEMENT.SITE,
    group_id: string
    name?: string
    type?: SERVICE_TASK | string
    duration: number
    sub_operations: { id: string, unit_id: string }[]
    error?: any
    note?: string
}

export interface IErrorForm {
    [rule: string]: NotifyCode
}

export interface IDensityMap {
    [treatmentTypes: string]: {
        [avgWeight: string]: [number, number]
    }
}

export interface IMaterial {
    id: string
    name: string
    type?: TYPE_MATERIAL_TRANSPORT
}

export interface IFishGroup {
    close_date: string
    group_id: string
    group_name: string
    harvest_count: number
    is_close: boolean
    is_merge: boolean
    open_date: string
    root_group: string
    site_id: string
    site_name: string
    mortality_percent: string
}

export interface IWeatherData {
    wind_speed?: any[],
    wind_direction?: any[],
    gust?: any[],
    waves_height?: any[],
    waves_direction?: any[],
    waves_period?: any[],
    temp?: any[],
    sea_temp?: any[],
}

export interface IServiceRequest {
    id: string
    name: string
    prod_area?: string
    type?: MAP_ELEMENT
    tenant_id?: string
    description?: string
    operations?: {
        operation_code: string
        id: string
        status: OP_STATUS
        est_start_time: number
        est_finish_time: number
        vessel_id: string
    }[]
    site_id?: string
    start_time?: number
    finish_time?: number
    vessel_resources?: string[]
}

export type TCrewTask = NonNullable<IService["crew_tasks"]>[0];

export interface IFile2 {
    key: string
    name: string
}
export interface IOperationUnitSummary2 {
    unit_id: string
    plan: IUnitSummary
    actual: IUnitSummary
}

export interface IHarvestTank2 {
    id: string
    name: string
    density?: number
    real_density?: number
    max_density?: number
    total_weight?: number
    fish_amount?: number
}

export interface ICrewTask2 {
    id: string
    description: string
    est_start_time?: number
    est_finish_time?: number
    real_start_time?: number
    real_finish_time?: number
}

export interface IRealTreatmentUnit2 {
    unit_id: string
    fish_amount?: number
    total_weight?: number
    avg_weight?: number
    tasks?: {
        treatment_type?: TREATMENT_TYPE
        type: TASK_TYPE
        destination_name?: string
    }[]
    round_number?: number
    dead_fish?: number
    mortality_increase?: number
    lice_reduction?: {
        stationary_lice?: number
        mobile_lice?: number
        female_lice?: number
    }
}

export interface ISubDestination2 extends IUnit {
    delivery_id?: string
    delivery_name?: string
}

export interface IBaseRouteOperation2 extends IDetailOperation {
    router_id?: string
    prev_op_router_id?: string
    start_place_id?: string
    start_place_field?: string
}

export interface IHarvest2 extends IBaseRouteOperation2 {
    duration_est?: number
    operation_type: OPERATION_TYPE.HARVEST
    site_id: string
    site_name: string
    sub_operations: IUnit[]

    factory_id: string
    factory_name: string

    tanks: IHarvestTank2[]
    total_weight: number
    fish_amount: number

    is_waiting_unit?: boolean
    op_running_mode?: OP_RUNNING_MODE
    isInternal?: boolean

    site?: ISite
    source?: ISite | any
    sites?: IIdName[]
    factory?: any
    destination?: any

    source_site_type?: SITE_TYPE
    total_amount?: number
    delivery_types?: Partial<IDeliveryType>[]

    is_journey_to_site?: boolean
    is_cleaning?: boolean

    error_detail?: any
    summary?: IOperationUnitSummary2[]
}

export interface ITreatment2 extends IDetailOperation {
    duration_est?: number
    operation_type: OPERATION_TYPE.TREATMENT
    site: ISite
    site_name: string
    sub_operations: IUnit[]

    support_vessel?: IVessel | any
    is_cleaning: boolean

    site_id?: string
    tasks: TTaskTreatment[]

    unit_order?: {
        unit_id: string
        id: string
    }[]

    files?: IFile2[]

    round_map?: Record<string, IRoundPlan>
    real_round_map?: Record<string, IRoundPlan & { start_time?: number, finish_time?: number }>
    real_sub_operations?: IRealTreatmentUnit2[]

    duration?: number
    dead_fish?: number
}

export interface ITransport2 extends IBaseRouteOperation2 {
    operation_type: OPERATION_TYPE.TRANSPORT
    source_id: string
    source_name: string
    source?: { id: string, name: string }

    destination_id?: string
    destination_name?: string
    destination?: { id: string, name: string }
    sub_destinations?: ISubDestination2[]

    source_site_type?: SITE_TYPE
    destination_site_type?: SITE_TYPE
    total_weight?: number
    sub_operations?: IUnit[]
}

export interface IEvent2 extends IDetailOperation {
    operation_type: OPERATION_TYPE.EVENT
    event_type: EVENT_TYPE
    note: string
    parallel?: boolean
    pois?: IPois[] | IIdName[]
}

export interface IService2 extends IDetailOperation {
    duration_est?: number
    operation_type: OPERATION_TYPE.SERVICE
    files?: IFile2[]
    parallel?: boolean
    site_id?: string
    site_name?: string
    sub_operations: IUnit[]
    tasks?: ITaskService[]
    site?: {
        id: string
        name: string
    }

    request_tab_id?: string
    crew_tasks?: ICrewTask2[]
}

export interface ISupportLocation2 {
    id: string
    name: string
    type: MAP_ELEMENT.SITE | MAP_ELEMENT.FACTORY | MAP_ELEMENT.POI
    sub_locations?: IIdName[]
}

export interface ISupportOp2 extends IDetailOperation {
    operation_type: OPERATION_TYPE.SUPPORT
    related_id: string
    locations: ISupportLocation2[]
}

export type TOperation2 =
    | IHarvest2
    | ITreatment2
    | ITransport2
    | IEvent2
    | IService2
    | ISupportOp2

export interface IOperation2<T extends TOperation2 = TOperation2> {
    activity_log: IActivityLog[]
    operation: T;
    vessel: IVessel

    status?: VALIDATION_STATUS
    message?: string
    isShow?: boolean
    loading?: boolean

    action_type?: OPERATION_CONFIRM_STATUS
    action?: DELETE_TYPE

    est_start_time?: number
    checklists_v2?: IChecklist[]

    route?: IRoute
}

export type TOperationData =
    | IOperation2<IHarvest2>
    | IOperation2<ITreatment2>
    | IOperation2<ITransport2>
    | IOperation2<IEvent2>
    | IOperation2<IService2>
    | IOperation2<ISupportOp2>;

export type TOperationLoose =
    IDetailOperation &
    Partial<IHarvest2> &
    Partial<ITreatment2> &
    Partial<ITransport2> &
    Partial<IEvent2> &
    Partial<IService2> &
    Partial<ISupportOp2>;

export interface ISensorItem {
    id: number;
    name: string;
    label: string;
    unit?: string;
    type: SensorGroupType;
    data: {
        timestamp: string;
        value: number;
    }[];
}

export interface ITankProcessData {
    [sensorType: string]: ISensorItem[];
}

export interface FleetUtilizationItem {
    vessel_id: string;

    operation: number;
    hygine: number;
    maintenance: number;
    idle: number;
    travelling: number;

    // "0", "1", "3" Operation type
    // "4_16aeb385-f778-431e-a5eb-a84ff8cef86a"
    [key: string]: string | number;
}
